import { TrendingUp, TrendingDown } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  badge?: {
    text: string;
    variant: 'success' | 'error' | 'warning' | 'info';
  };
  action?: {
    label: string;
    onClick: () => void;
  };
  valueColor?: string;
}

const badgeVariants = {
  success: 'text-success bg-success/10',
  error: 'text-error bg-error/10',
  warning: 'text-warning bg-warning/10',
  info: 'text-info bg-info/10',
} as const;

export function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  badge,
  action,
  valueColor,
}: MetricCardProps) {
  return (
    <div className="group flex flex-col gap-2 rounded-xl border border-border bg-card p-4 shadow-card transition-shadow duration-200 hover:shadow-card-hover sm:p-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold leading-tight text-text-secondary sm:text-base">
          {title}
        </p>
        <Icon size={20} className="shrink-0 text-text-secondary" />
      </div>

      {/* Value */}
      <p
        className={`text-3xl font-bold tabular-nums tracking-tight sm:text-4xl ${
          valueColor || 'text-text-title'
        }`}
      >
        {value}
      </p>

      {/* Footer */}
      <div className="mt-1 flex items-center justify-between gap-3">
        <p className="text-xs text-text-secondary">{subtitle}</p>

        {trend && (
          <span
            className={`flex items-center gap-1 text-sm font-semibold tabular-nums ${
              trend.isPositive ? 'text-success' : 'text-error'
            }`}
          >
            {trend.isPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {trend.isPositive ? '+' : ''}
            {trend.value}
          </span>
        )}

        {badge && (
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${badgeVariants[badge.variant]}`}
          >
            {badge.text}
          </span>
        )}

        {action && (
          <button
            onClick={action.onClick}
            className="text-sm font-semibold text-primary transition-colors hover:text-primary/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}
